import React from 'react';
import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { Moment } from 'moment-timezone';
import { FridgeTemp, ParsedSensorInfo } from '../atoms-types/eSensors';
import FridgeTempStats from '../charts-graphs/fridgeTempStats';
import SensorIdentityTooltip from './SensorIdentityTooltip';
import {
  FRIDGE_WARNING_F,
  getDisplayClass,
  getDisplayLocation,
  getHvacStatus,
  getWarningThreshold,
  HVAC_STATUS_COLORS,
  HVAC_STATUS_LABELS,
} from './hvacStatus';
import { formatLastSeen, SENSOR_OFFLINE_BORDER_COLOR } from '../atoms-types/sensorFreshness';

interface HvacSelectedPanelProps {
  sensor: ParsedSensorInfo | null;
  startDateTime: Moment;
  endDateTime: Moment;
}

const HvacSelectedPanel: React.FC<HvacSelectedPanelProps> = ({
  sensor,
  startDateTime,
  endDateTime,
}) => {
  if (!sensor) {
    return (
      <Card sx={{ height: '100%', minHeight: 280 }}>
        <CardContent
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="text.secondary">Select a unit to inspect</Typography>
        </CardContent>
      </Card>
    );
  }

  const tempF = (sensor.jInfo as FridgeTemp | undefined)?.TempF;
  const battery = (sensor.jInfo as FridgeTemp | undefined)?.Battery;
  const thresholds = { warningF: sensor.warningF, alarmF: sensor.alarmF };
  const status = getHvacStatus(typeof tempF === 'number' ? tempF : null, sensor.LastRx, thresholds);
  const statusColor = HVAC_STATUS_COLORS[status];
  // An offline sensor's last reading is stale; show 0 instead of the stale temp.
  const offline = status === 'offline';
  const displayTempF = offline ? 0 : tempF;
  const name = getDisplayLocation(sensor) || sensor.deviceEUI;
  const classLabel = getDisplayClass(sensor);
  const lastSeen = formatLastSeen(sensor.LastRx);

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 280,
        borderTop: offline
          ? `4px dashed ${SENSOR_OFFLINE_BORDER_COLOR}`
          : `4px solid ${statusColor}`,
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
            Selected Unit
          </Typography>
          <SensorIdentityTooltip sensor={sensor}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                width: 'fit-content',
                color: offline ? statusColor : 'text.primary',
              }}
            >
              {name}
            </Typography>
          </SensorIdentityTooltip>
          {classLabel ? (
            <Typography
              variant="body2"
              sx={{ color: offline ? statusColor : 'text.secondary' }}
            >
              {classLabel}
            </Typography>
          ) : null}
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: '2.75rem',
              fontWeight: 700,
              lineHeight: 1,
              color:
                status === 'alarm'
                  ? statusColor
                  : offline
                    ? statusColor
                    : 'text.primary',
            }}
          >
            {typeof displayTempF === 'number' ? `${displayTempF.toFixed(1)}°F` : '—'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: statusColor,
              }}
            />
            <Typography sx={{ fontWeight: 600, color: statusColor }}>
              {HVAC_STATUS_LABELS[status]}
            </Typography>
            {status === 'alarm' ? (
              <Typography variant="body2" sx={{ color: statusColor, fontWeight: 600 }}>
                · Above threshold
              </Typography>
            ) : null}
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: status === 'offline' ? statusColor : 'text.secondary',
              fontWeight: status === 'offline' ? 600 : 400,
            }}
          >
            Last seen {lastSeen}
          </Typography>
        </Box>

        <Divider />

        <FridgeTempStats
          deviceEUI={sensor.deviceEUI}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          nominalMax={getWarningThreshold(thresholds) || FRIDGE_WARNING_F}
          unit="°F"
          liveTempF={offline ? null : (typeof tempF === 'number' ? tempF : null)}
        />

        {battery ? (
          <>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                Battery
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {battery}
              </Typography>
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default HvacSelectedPanel;
